import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CompanyContractualTermsDto } from '@app/features/admin/customer-profile/models/company-contractual-terms-dto.model';
import { CompanyContractualTermsViewModel } from '@app/features/admin/customer-profile/models/company-contractual-terms-view-model.model';
import { CustomerTruckingRouteContractDto } from '@app/features/admin/customer-profile/models/customer-trucking-route-contract-dto.model';
import { CustomerTruckingRouteContractFilterLookupsDto } from '@app/features/admin/customer-profile/models/customer-trucking-route-contract-filter-lookups-dto.model';
import { CustomerTruckingRouteContractQueryViewModel } from '@app/features/admin/customer-profile/models/customer-trucking-route-contract-query-view-model.model';
import { CustomerTruckingRouteContractUpdateViewModel } from '@app/features/admin/customer-profile/models/customer-trucking-route-contract-update-view-model.model';
import { CustomerInspectionAndApprovalQueryViewModel } from '@app/features/admin/customer-profile/models/customer-inspection-and-approval-query-view-model.model';
import { CustomerInspectionAndApprovalContractUpdateViewModel } from '@app/features/admin/customer-profile/models/customer-inspection-and-approval-contract-update-view-model.model';
import { TruckingPriceListSearchResultDto } from '@app/features/admin/service-providers/models/trucking-pricelist-search-result.model';
import { TruckingPriceListSearchViewModel } from '@app/features/admin/service-providers/models/trucking-pricelist-search.model';
import { InspectionAndApprovalSearchResultDto } from '../customer-profile/models/inspection-and-approval-search-result.model';
import { InspectionAndApprovalSearchViewModel } from '../customer-profile/models/inspection-and-approval-search.model';
import { PagedResult } from '@app/shared';
import { environment } from '@environments/environment';
import { Observable, Subject } from 'rxjs';
import { CustomerContact } from '../customer-profile/models/customer-contact.model';
import { CustomerServiceBundleModel } from '../customer-profile/models/customer-service-bundle.model';
import { CustomerSelectedService } from '../models/customer-selected-service.model';
import { CustomerIorOrEorService } from '../customer-profile/models/customer-ior-or-eor-service.model';
import { IorOrEorCustomerServiceLookups } from '../customer-profile/models/ior-or-eor-customer-service-lookups.model';
import { AccountManagementCustomerServiceLookups } from '../customer-profile/models/account-management-customer-service-lookups.model';
import { CustomerAccountManagementModel } from '../customer-profile/models/customer-account-management.model';
import { ProductRegistrationServiceCustomerDto } from '../customer-profile/models/product-registration-service-customer.model';
import { UpdateProductRegistrationServiceCustomerDto } from '../customer-profile/models/update-product-registration-service-customer.model';
import { CustomerCustomsClearanceRouteDetailsDto } from '../customer-profile/models/customer-customs-clearance-route-details-dto.model';
import { CustomerCustomsClearanceRateQueryViewModel } from '../customer-profile/models/customer-customs-clearance-rate-query-view-model.model';
import { CustomerCustomsClearanceRateFilterLookupsDto } from '../customer-profile/models/customer-customs-clearance-rate-filter-lookups-dto.model';
import { CustomsClearanceRouteSearchViewModel } from '../service-providers/models/customs-clearance-route-search-view-model.model';
import { CustomsClearancePriceListSearchResultDto } from '../service-providers/models/customs-clearance-price-list-search-result.model';
import { ServiceProviderInspectionAndApproval } from '../service-providers/models/service-provider-inspection-and-approval.model';
import { CustomerInspectionAndApprovalContractDto } from '../customer-profile/models/customer-inspection-and-approval-contract-dto.model';

@Injectable()
export class CustomerServicesService {
  /** Stub: replace with real logic when customer services are implemented. */
  selectedServiceChanged = new Subject<void>();

  baseURL = environment.apiUrl + 'customerSelectedServices';
  contactsBaseURL = environment.apiUrl + 'customerContact';
  private http = inject(HttpClient);

  getCustomerContacts(companyId: number): Observable<CustomerContact[]> {
    return this.http.get<CustomerContact[]>(
      `${this.contactsBaseURL}/Company/${companyId}`
    );
  }

  getCustomerContactById(contactId: number): Observable<CustomerContact> {
    return this.http.get<CustomerContact>(
      `${this.contactsBaseURL}/${contactId}`
    );
  }

  addCustomerContact(
    payload: Partial<CustomerContact>
  ): Observable<CustomerContact> {
    return this.http.post<CustomerContact>(`${this.contactsBaseURL}`, payload);
  }

  updateCustomerContact(
    contactId: number,
    payload: Partial<CustomerContact>
  ): Observable<CustomerContact> {
    return this.http.put<CustomerContact>(
      `${this.contactsBaseURL}/${contactId}`,
      payload
    );
  }

  deleteCustomerContact(contactId: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.contactsBaseURL}/${contactId}`);
  }

  getCompanyContractualTerms(
    companyId: number
  ): Observable<CompanyContractualTermsDto | null> {
    return this.http.get<CompanyContractualTermsDto | null>(
      `${this.baseURL}/${companyId}/CompanyContractualTerm`
    );
  }

  saveCompanyContractualTerms(
    payload: CompanyContractualTermsViewModel
  ): Observable<CompanyContractualTermsViewModel> {
    return this.http.post<CompanyContractualTermsViewModel>(
      `${this.baseURL}/CompanyContractualTerm`,
      payload
    );
  }

  updateCompanyContractualTerms(
    companyId: number,
    payload: CompanyContractualTermsViewModel
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseURL}/${companyId}/CompanyContractualTerm`,
      payload
    );
  }

  searchTruckingPriceLists(
    companyId: number,
    searchViewModel: TruckingPriceListSearchViewModel
  ): Observable<TruckingPriceListSearchResultDto[]> {
    return this.http.post<TruckingPriceListSearchResultDto[]>(
      `${this.baseURL}/${companyId}/SearchTruckingPriceLists`,
      searchViewModel
    );
  }

  searchInspectionsAndApprovals(
    companyId: number,
    searchViewModel: InspectionAndApprovalSearchViewModel
  ): Observable<InspectionAndApprovalSearchResultDto[]> {
    return this.http.post<InspectionAndApprovalSearchResultDto[]>(
      `${this.baseURL}/${companyId}/SearchInspectionsAndApprovals`,
      searchViewModel
    );
  }

  updateCustomerSelectedServices(
    customerSelectedService: CustomerSelectedService
  ): Observable<any> {
    return this.http.put<any>(
      `${this.baseURL}/SelectedService`,
      customerSelectedService
    );
  }

  getCustomerTruckingRouteContractsPaged(
    companyId: number,
    query: CustomerTruckingRouteContractQueryViewModel
  ): Observable<PagedResult<CustomerTruckingRouteContractDto>> {
    const url = `${this.baseURL}/${companyId}/CustomerTruckingRouteContracts/paged`;
    let params = new HttpParams()
      .set('pageIndex', query.pageIndex.toString())
      .set('pageSize', query.pageSize.toString());

    if (query.isDomestic !== undefined) {
      params = params.set('isDomestic', query.isDomestic.toString());
    }
    if (query.search) {
      params = params.set('search', query.search);
    }
    if (query.searchKey !== undefined) {
      params = params.set('searchKey', query.searchKey.toString());
    }
    if (query.serviceProviderName) {
      params = params.set('serviceProviderName', query.serviceProviderName);
    }
    if (query.pickupFrom) {
      params = params.set('pickupFrom', query.pickupFrom);
    }
    if (query.deliverTo) {
      params = params.set('deliverTo', query.deliverTo);
    }
    if (query.loadType) {
      params = params.set('loadType', query.loadType);
    }

    return this.http.get<PagedResult<CustomerTruckingRouteContractDto>>(url, {
      params,
    });
  }

  getCustomerTruckingRouteContractFilterLookups(
    companyId: number,
    isDomestic?: boolean
  ): Observable<CustomerTruckingRouteContractFilterLookupsDto> {
    const url = `${this.baseURL}/${companyId}/CustomerTruckingRouteContracts/filterLookups`;
    let params = new HttpParams();

    if (isDomestic !== undefined) {
      params = params.set('isDomestic', isDomestic.toString());
    }

    return this.http.get<CustomerTruckingRouteContractFilterLookupsDto>(url, {
      params,
    });
  }

  deleteCustomerTruckingRouteContract(
    companyId: number,
    contractId: number
  ): Observable<boolean> {
    const url = `${this.baseURL}/${companyId}/CustomerTruckingRouteContracts/${contractId}`;
    return this.http.delete<boolean>(url);
  }

  updateCustomerTruckingRouteContract(
    companyId: number,
    contractId: number,
    payload: CustomerTruckingRouteContractUpdateViewModel
  ): Observable<void> {
    const url = `${this.baseURL}/${companyId}/CustomerTruckingRouteContracts/${contractId}`;
    return this.http.put<void>(url, payload);
  }

  addCustomerTruckingPriceListContract(
    companyId: number,
    serviceProviderId: string,
    serviceProviderName: string,
    truckingPricelist: any
  ): Observable<CustomerTruckingRouteContractDto> {
    const params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('serviceProviderId', serviceProviderId)
      .set('serviceProviderName', serviceProviderName);
    return this.http.post<CustomerTruckingRouteContractDto>(
      `${this.baseURL}/TruckingPriceListContract`,
      truckingPricelist,
      { params }
    );
  }

  addCustomerCustomsClearancePriceListRate(
    companyId: number,
    serviceProviderId: string,
    customsClearancePricelistId: string
  ): Observable<CustomerCustomsClearanceRouteDetailsDto> {
    const payload = {
      companyId,
      serviceProviderId,
      customsClearancePricelistId,
    };
    return this.http.post<CustomerCustomsClearanceRouteDetailsDto>(
      `${this.baseURL}/CustomerCustomsClearanceRate`,
      payload
    );
  }

  deleteCustomerCustomsClearanceRate(rateId: number): Observable<boolean>;
  deleteCustomerCustomsClearanceRate(
    companyId: number,
    rateId: number
  ): Observable<boolean>;
  deleteCustomerCustomsClearanceRate(
    companyIdOrRateId: number,
    rateId?: number
  ): Observable<boolean> {
    if (rateId != null) {
      return this.http.delete<boolean>(
        `${this.baseURL}/${companyIdOrRateId}/CustomerCustomsClearanceRates/${rateId}`
      );
    }

    return this.http.delete<boolean>(
      `${this.baseURL}/CustomerCustomsClearanceRate/${companyIdOrRateId}`
    );
  }

  addCustomerInspectionAndApprovalContract(
    companyId: number,
    serviceProviderId: string,
    serviceProviderName: string,
    inspectionAndApproval: ServiceProviderInspectionAndApproval
  ): Observable<CustomerInspectionAndApprovalContractDto> {
    const params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('serviceProviderId', serviceProviderId)
      .set('serviceProviderName', serviceProviderName);
    return this.http.post<CustomerInspectionAndApprovalContractDto>(
      `${this.baseURL}/InspectionAndApprovalContract`,
      inspectionAndApproval,
      { params }
    );
  }

  searchCustomsClearancePriceLists(
    query: CustomsClearanceRouteSearchViewModel
  ): Observable<CustomsClearancePriceListSearchResultDto[]> {
    return this.http.post<CustomsClearancePriceListSearchResultDto[]>(
      `${this.baseURL}/SearchCustomsClearancePriceLists`,
      query
    );
  }

  getCustomerServiceBundle(
    companyId: number
  ): Observable<CustomerServiceBundleModel> {
    return this.http.get<CustomerServiceBundleModel>(
      `${this.baseURL}/${companyId}/CustomerServiceBundle`
    );
  }

  createCustomerServiceBundle(
    model: CustomerServiceBundleModel,
    companyId: number,
    ruleType: string[]
  ): Observable<CustomerServiceBundleModel> {
    model.companyId = companyId;
    model.rules = ruleType;
    return this.http.post<CustomerServiceBundleModel>(
      `${this.baseURL}/CustomerServiceBundle`,
      model
    );
  }

  updateCustomerServiceBundle(
    model: CustomerServiceBundleModel,
    companyId: number,
    ruleType: string[]
  ): Observable<CustomerServiceBundleModel> {
    model.companyId = companyId;
    model.rules = ruleType;
    return this.http.put<CustomerServiceBundleModel>(
      `${this.baseURL}/CustomerServiceBundle`,
      model
    );
  }
  getCustomerTruckingRouteContractById(
    companyId: number,
    contractId: number
  ): Observable<CustomerTruckingRouteContractDto> {
    const url = `${this.baseURL}/${companyId}/CustomerTruckingRouteContracts/${contractId}`;
    return this.http.get<CustomerTruckingRouteContractDto>(url);
  }

  getCustomerIorOrEorService(
    companyId: number,
    freePLServiceId: number
  ): Observable<CustomerIorOrEorService> {
    return this.http.get<CustomerIorOrEorService>(
      `${this.baseURL}/${companyId}/IorOrEorServiceForCustomer/${freePLServiceId}`
    );
  }

  updateCustomerIorOrEorService(
    customerIorEorService: CustomerIorOrEorService
  ): Observable<any> {
    return this.http.put(
      `${this.baseURL}/IorOrEorServiceForCustomer`,
      customerIorEorService
    );
  }

  saveIorOrEorServiceForCustomer(
    serviceType: string,
    customerIorOrEorService: CustomerIorOrEorService
  ): Observable<any> {
    return this.http.post<any>(
      `${this.baseURL}/IorOrEorServiceForCustomer/${serviceType}`,
      customerIorOrEorService
    );
  }

  getIorOrEorLookups(
    serviceType: string
  ): Observable<IorOrEorCustomerServiceLookups> {
    return this.http.get<IorOrEorCustomerServiceLookups>(
      `${this.baseURL}/IorOrEorLookups/${serviceType}`
    );
  }

  getCustomerAccountManagement(
    companyId: number
  ): Observable<CustomerAccountManagementModel> {
    return this.http.get<CustomerAccountManagementModel>(
      `${this.baseURL}/${companyId}/AccountManagement`
    );
  }

  getAccountManagementLookups(): Observable<AccountManagementCustomerServiceLookups> {
    return this.http.get<AccountManagementCustomerServiceLookups>(
      `${this.baseURL}/AccountManagementLookups`
    );
  }

  createCustomerAccountManagement(
    model: CustomerAccountManagementModel,
    companyId: number
  ): Observable<CustomerAccountManagementModel> {
    model.companyId = companyId;
    return this.http.post<CustomerAccountManagementModel>(
      `${this.baseURL}/AccountManagement`,
      model
    );
  }

  updateCustomerAccountManagement(
    model: CustomerAccountManagementModel,
    companyId: number
  ): Observable<CustomerAccountManagementModel> {
    model.companyId = companyId;
    return this.http.put<CustomerAccountManagementModel>(
      `${this.baseURL}/AccountManagement`,
      model
    );
  }

  getProductRegistrationServiceCustomers(
    companyId: number
  ): Observable<ProductRegistrationServiceCustomerDto[]> {
    return this.http.get<ProductRegistrationServiceCustomerDto[]>(
      `${this.baseURL}/ProductRegistrationsServiceCustomer/${companyId}`
    );
  }

  updateProductRegistrationCustomer(
    customerId: number,
    productRegistrationServiceCustomer: UpdateProductRegistrationServiceCustomerDto[]
  ): Observable<UpdateProductRegistrationServiceCustomerDto> {
    return this.http.put<UpdateProductRegistrationServiceCustomerDto>(
      `${this.baseURL}/ProductRegistrationsServiceCustomer/${customerId}`,
      productRegistrationServiceCustomer
    );
  }

  getCustomerCustomsClearanceRatesPaged(
    companyId: number,
    query: CustomerCustomsClearanceRateQueryViewModel
  ): Observable<PagedResult<CustomerCustomsClearanceRouteDetailsDto>> {
    const url = `${this.baseURL}/${companyId}/CustomerCustomsClearanceRates/paged`;
    let params = new HttpParams()
      .set('pageIndex', (query.pageIndex || 1).toString())
      .set('pageSize', (query.pageSize || 10).toString());

    if (query.search) {
      params = params.set('search', query.search);
    }
    if (query.searchKey !== undefined) {
      params = params.set('searchKey', query.searchKey.toString());
    }
    if (query.sortBy !== undefined) {
      params = params.set('sortBy', query.sortBy.toString());
    }
    if (query.sortDirection !== undefined) {
      params = params.set('sortDirection', query.sortDirection.toString());
    }
    if (query.serviceProviderName) {
      params = params.set('serviceProviderName', query.serviceProviderName);
    }
    if (query.freightType) {
      params = params.set('freightType', query.freightType);
    }
    if (query.transactionType) {
      params = params.set('transactionType', query.transactionType);
    }
    if (query.rateType) {
      params = params.set('rateType', query.rateType);
    }
    if (query.portOfClearance) {
      params = params.set('portOfClearance', query.portOfClearance);
    }

    return this.http.get<PagedResult<CustomerCustomsClearanceRouteDetailsDto>>(
      url,
      { params }
    );
  }

  getCustomerCustomsClearanceRateFilterLookups(
    companyId: number
  ): Observable<CustomerCustomsClearanceRateFilterLookupsDto> {
    const url = `${this.baseURL}/${companyId}/CustomerCustomsClearanceRates/filterLookups`;
    return this.http.get<CustomerCustomsClearanceRateFilterLookupsDto>(url);
  }

  getCustomerCustomsClearanceRateById(
    rateId: number
  ): Observable<CustomerCustomsClearanceRouteDetailsDto> {
    const url = `${this.baseURL}/CustomerCustomsClearanceRate/${rateId}`;
    return this.http.get<CustomerCustomsClearanceRouteDetailsDto>(url);
  }

  updateCustomerCustomsClearanceRate(
    rateId: number,
    payload: CustomerCustomsClearanceRouteDetailsDto
  ): Observable<CustomerCustomsClearanceRouteDetailsDto> {
    const url = `${this.baseURL}/CustomerCustomsClearanceRate/${rateId}`;
    return this.http.put<CustomerCustomsClearanceRouteDetailsDto>(url, payload);
  }

  exportCustomerCustomsClearanceRates(
    companyId: number,
    query: CustomerCustomsClearanceRateQueryViewModel
  ): Observable<Blob> {
    const url = `${this.baseURL}/${companyId}/CustomerCustomsClearanceRates/export`;
    let params = new HttpParams()
      .set('pageIndex', (query.pageIndex || 1).toString())
      .set('pageSize', (query.pageSize || 9999).toString())
      .set('download', 'true');

    if (query.search) {
      params = params.set('search', query.search);
    }
    if (query.searchKey !== undefined) {
      params = params.set('searchKey', query.searchKey.toString());
    }
    if (query.sortBy !== undefined) {
      params = params.set('sortBy', query.sortBy.toString());
    }
    if (query.sortDirection !== undefined) {
      params = params.set('sortDirection', query.sortDirection.toString());
    }
    if (query.serviceProviderName) {
      params = params.set('serviceProviderName', query.serviceProviderName);
    }
    if (query.freightType) {
      params = params.set('freightType', query.freightType);
    }
    if (query.transactionType) {
      params = params.set('transactionType', query.transactionType);
    }
    if (query.rateType) {
      params = params.set('rateType', query.rateType);
    }
    if (query.portOfClearance) {
      params = params.set('portOfClearance', query.portOfClearance);
    }

    return this.http.get(url, { params, responseType: 'blob' });
  }

  getCustomerInspectionAndApprovalContractsPaged(
    companyId: number,
    query: CustomerInspectionAndApprovalQueryViewModel
  ): Observable<PagedResult<CustomerInspectionAndApprovalContractDto>> {
    const url = `${this.baseURL}/${companyId}/CustomerInspectionAndApprovals/paged`;
    let params = new HttpParams()
      .set('pageIndex', (query.pageIndex ?? 0).toString())
      .set('pageSize', (query.pageSize ?? 10).toString());

    if (query.search) {
      params = params.set('search', query.search);
    }
    if (query.searchKey !== undefined) {
      params = params.set('searchKey', query.searchKey.toString());
    }

    return this.http.get<PagedResult<CustomerInspectionAndApprovalContractDto>>(
      url,
      { params }
    );
  }

  getCustomerInspectionAndApprovalContractById(
    companyId: number,
    contractId: number
  ): Observable<CustomerInspectionAndApprovalContractDto> {
    const url = `${this.baseURL}/${companyId}/CustomerInspectionAndApprovals/${contractId}`;
    return this.http.get<CustomerInspectionAndApprovalContractDto>(url);
  }

  updateCustomerInspectionAndApprovalContract(
    companyId: number,
    contractId: number,
    payload: CustomerInspectionAndApprovalContractUpdateViewModel
  ): Observable<void> {
    const url = `${this.baseURL}/${companyId}/CustomerInspectionAndApprovals/${contractId}`;
    return this.http.put<void>(url, payload);
  }

  deleteCustomerInspectionAndApprovalContract(
    companyId: number,
    contractId: number
  ): Observable<boolean> {
    const url = `${this.baseURL}/${companyId}/CustomerInspectionAndApprovals/${contractId}`;
    return this.http.delete<boolean>(url);
  }
}
